import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Radio, message } from "antd";
import { useRecoilState } from "recoil";
import { brandKitsState } from "../../../../../state/atoms";
import BrandKitMultiSelect from "./BrandKitMultiSelect";
import BrandKitApi, { DrdCopyStrategy } from "../../../../../api/BrandKitApi";
import "./CopyToBrandKitModal.scss";
import "./CopyIconMappingsModal.scss";

interface CopyIconMappingsModalProps {
  open: boolean;
  brandKitId: string;
  attributes: string[];
  onClose: () => void;
  onCopied?: () => void;
}

export default function CopyIconMappingsModal({
  open,
  brandKitId,
  attributes,
  onClose,
  onCopied,
}: CopyIconMappingsModalProps) {
  const [brandKits] = useRecoilState(brandKitsState);
  const [selected, setSelected] = useState<string[]>([]);
  const [strategy, setStrategy] = useState<DrdCopyStrategy>("FILL_UNMAPPED");
  const [copying, setCopying] = useState(false);
  const [affectedKitCount, setAffectedKitCount] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setSelected([]);
      setStrategy("FILL_UNMAPPED");
      setAffectedKitCount(null);
    }
  }, [open]);

  const otherKits = useMemo(
    () => brandKits.filter((b) => b.id && b.id !== brandKitId),
    [brandKits, brandKitId]
  );

  useEffect(() => {
    if (!open || strategy !== "REPLACE" || selected.length === 0) {
      setAffectedKitCount(null);
      return;
    }
    let active = true;
    BrandKitApi.copyDrdIcons(brandKitId, {
      targetBrandKitIds: selected,
      attributes,
      strategy: "REPLACE",
      dryRun: true,
    })
      .then((resp) => active && setAffectedKitCount(resp.data.affectedKitCount))
      .catch(() => active && setAffectedKitCount(null));
    return () => {
      active = false;
    };
  }, [open, strategy, selected, brandKitId, attributes]);

  const selectedKits = otherKits.filter((b) => selected.includes(b.id!));
  const selectedNames = selectedKits.map((b) => b.name || "Untitled");

  const confirmText =
    selected.length === 1
      ? `Copy to ${selectedNames[0]}`
      : selected.length > 1
      ? `Copy to ${selected.length} Brand Kits`
      : "Copy";

  function close() {
    if (copying) return;
    onClose();
  }

  function handleCopy() {
    if (selected.length === 0 || attributes.length === 0) return;
    setCopying(true);
    BrandKitApi.copyDrdIcons(brandKitId, {
      targetBrandKitIds: selected,
      attributes,
      strategy,
      dryRun: false,
    })
      .then((resp) => {
        const { sourceMappingCount, targetKitCount } = resp.data;
        const iconNoun = `icon${sourceMappingCount === 1 ? "" : "s"}`;
        const kitNoun = `Brand Kit${targetKitCount === 1 ? "" : "s"}`;
        const text = `${sourceMappingCount} ${iconNoun} copied to ${targetKitCount} ${kitNoun}`;
        message.success(text);
        onCopied?.();
        onClose();
      })
      .catch(() => message.error("Failed to copy icon mappings"))
      .finally(() => setCopying(false));
  }

  const showReplaceWarning =
    strategy === "REPLACE" && affectedKitCount != null && affectedKitCount > 0;

  return (
    <Modal
      className="copy-to-brand-kit-modal copy-icon-mappings-modal"
      open={open}
      onCancel={close}
      footer={null}
      width={600}
      destroyOnClose
    >
      <header>
        <h2>Copy mappings to Brand Kits</h2>
        <p>Copies the selected Icon Attribute mappings to other Brand Kits.</p>
        {attributes.length > 0 && (
          <span className="copy-to-brand-kit-count">
            {attributes.length} icon{attributes.length === 1 ? "" : "s"} selected
          </span>
        )}
      </header>

      <BrandKitMultiSelect kits={otherKits} selected={selected} onChange={setSelected} />

      <div className="copy-icon-mappings-strategy">
        <Radio.Group value={strategy} onChange={(e) => setStrategy(e.target.value)}>
          <Radio value="FILL_UNMAPPED">
            <span className="copy-icon-mappings-strategy-label">Keep existing icons</span>
            <span className="copy-icon-mappings-strategy-hint">
              Only fills attributes that aren't mapped yet
            </span>
          </Radio>
          <Radio value="REPLACE">
            <span className="copy-icon-mappings-strategy-label">Replace with this kit's icons</span>
            <span className="copy-icon-mappings-strategy-hint">
              Overwrites icons already mapped in the target kits
            </span>
          </Radio>
        </Radio.Group>
      </div>

      {showReplaceWarning && (
        <Alert
          className="copy-icon-mappings-warning"
          type="warning"
          showIcon
          message={`Replace will overwrite existing icons in ${affectedKitCount} of ${selected.length} selected Brand Kit${
            selected.length === 1 ? "" : "s"
          }.`}
        />
      )}

      <footer>
        <Button size="large" onClick={close} disabled={copying}>
          Cancel
        </Button>
        <Button
          size="large"
          className="primary"
          onClick={handleCopy}
          loading={copying}
          disabled={selected.length === 0}
        >
          {confirmText}
        </Button>
      </footer>
    </Modal>
  );
}
